// Thin HTTP layer over admin-server's REST API.
//
// Design notes
// ────────────
//  * One module-level fetch wrapper; no DI of "different fetch impls"
//    or transformers. Tests stub the global fetch via vi.spyOn.
//  * Cookies are SameSite=Strict + HttpOnly; `credentials: 'include'`
//    is what asks the browser to attach them.
//  * Every mutating verb (POST/PUT/PATCH/DELETE) gets the
//    `X-Admin-Request: 1` header — the server's RequireCSRFHeader
//    middleware demands it. GET passes through without it (the
//    middleware skips safe verbs).
//  * 401 short-circuits via the `onUnauthorized` callback the auth
//    store wires up. We keep the callback indirection rather than
//    importing the store directly so api.ts stays state-free and
//    side-effect-free at module load — important for unit tests.

/** Methods the server treats as mutating. Drives the CSRF header. */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/** Header name + sentinel value — must match middleware.CSRFHeaderName/Value. */
export const CSRF_HEADER = 'X-Admin-Request'
export const CSRF_VALUE = '1'

/** All error shapes the API can produce, unified for the UI. */
export class ApiError extends Error {
  override readonly name = 'ApiError'
  constructor(
    /** HTTP status; 0 when no response (network / DNS / CORS). */
    public readonly status: number,
    /** Decoded body's `error` field, when present, else the HTTP status text. */
    message: string,
    /** Raw body for callers that want to render unknown shapes. */
    public readonly body: unknown,
  ) {
    super(message)
  }
}

interface RequestOptions {
  /** Query string keys; null/undefined values are dropped. */
  query?: Record<string, string | number | undefined | null>
  /** Body to JSON.stringify. Omit on GET. */
  body?: unknown
  /** Optional extra headers; do NOT set CSRF here — we add it. */
  headers?: Record<string, string>
  /** AbortController.signal — TanStack Query wires this through. */
  signal?: AbortSignal
}

/** Hook the auth store installs at app boot. Default = no-op. */
let onUnauthorized: () => void = () => {}
export function setOnUnauthorized(fn: () => void): void {
  onUnauthorized = fn
}

/**
 * Send one request. Returns parsed JSON (the empty string for 204).
 * Throws ApiError on anything non-2xx OR on a network failure.
 */
export async function request<T = unknown>(
  method: string,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(path, opts.query)
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers ?? {}),
  }
  let body: BodyInit | undefined
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }
  if (MUTATING_METHODS.has(method.toUpperCase())) {
    headers[CSRF_HEADER] = CSRF_VALUE
  }

  // Build RequestInit conditionally: with `exactOptionalPropertyTypes`
  // we can't pass `body: undefined` or `signal: undefined`; the property
  // must be absent entirely.
  const init: RequestInit = {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers,
    ...(body !== undefined ? { body } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  }

  let res: Response
  try {
    res = await fetch(url, init)
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : 'network error', null)
  }

  if (res.status === 401) {
    // Cookie expired / killed by a password change elsewhere → fire
    // the redirect hook BEFORE throwing so the route swap happens
    // before the caller's catch block runs (avoids a "you have no
    // user" flash on the soon-to-be-replaced page).
    onUnauthorized()
  }

  // 204 No Content: nothing to parse.
  if (res.status === 204) return undefined as T

  let parsed: unknown = null
  const text = await res.text()
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      // Server returned non-JSON for some reason — surface the raw
      // text so a misbehaving proxy is at least visible.
      parsed = text
    }
  }

  if (!res.ok) {
    const message = extractErrorMessage(parsed) ?? res.statusText
    throw new ApiError(res.status, message, parsed)
  }
  return parsed as T
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  let qs = ''
  if (query) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v === null || v === undefined || v === '') continue
      params.set(k, String(v))
    }
    const s = params.toString()
    if (s) qs = '?' + s
  }
  return path + qs
}

function extractErrorMessage(body: unknown): string | null {
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error: unknown }).error
    if (typeof e === 'string') return e
  }
  return null
}

/** Convenience helpers — one per verb. Keeps call sites terse. */
export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('POST', path, opts),
  patch: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('PATCH', path, opts),
  put: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('PUT', path, opts),
  delete: <T = unknown>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
}
